import { supabase } from '../utils/supabase';

export interface WaitlistSignup {
  email: string;
  first_name?: string;
  last_name?: string;
  source?: string;
}

export const waitlistService = {
  /**
   * Add a new email to the waitlist
   */
  async joinWaitlist(signup: WaitlistSignup): Promise<{ success: boolean; error?: string }> {
    try {
      const { error } = await supabase
        .from('waitlist')
        .insert([signup]);

      if (error) {
        // Handle duplicate email error gracefully
        if (error.code === '23505') { // Unique constraint violation
          return { success: false, error: 'This email is already on our waitlist!' };
        }
        return { success: false, error: error.message };
      }

      return { success: true };
    } catch (err) {
      console.error('Error joining waitlist:', err);
      return { success: false, error: 'Failed to join waitlist. Please try again.' };
    }
  },

  /**
   * Check if an email is already on the waitlist
   */
  async isOnWaitlist(email: string): Promise<boolean> {
    try {
      const { data, error } = await supabase
        .from('waitlist')
        .select('id')
        .eq('email', email)
        .single();

      if (error && error.code !== 'PGRST116') { // PGRST116 = no rows returned
        console.error('Error checking waitlist status:', error);
        return false;
      }

      return !!data;
    } catch (err) {
      console.error('Error checking waitlist status:', err);
      return false;
    }
  },

  /**
   * Get waitlist statistics (for admin use)
   */
  async getWaitlistStats(): Promise<{ total: number; recent: number } | null> {
    try {
      const { data: totalData, error: totalError } = await supabase
        .from('waitlist')
        .select('id', { count: 'exact' });

      if (totalError) {
        console.error('Error getting total waitlist count:', totalError);
        return null;
      }

      const { data: recentData, error: recentError } = await supabase
        .from('waitlist')
        .select('id')
        .gte('subscribed_at', new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString()); // Last 7 days

      if (recentError) {
        console.error('Error getting recent waitlist count:', recentError);
        return null;
      }

      return {
        total: totalData?.length || 0,
        recent: recentData?.length || 0
      };
    } catch (err) {
      console.error('Error getting waitlist stats:', err);
      return null;
    }
  }
};
